import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from "@mui/material";
import ImageResize from "image-resize";
import React, { ReactNode, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import getCroppedImg from "./cropImage";
import { Button, Chip } from "@mui/joy";
import DialogClose from "../dialogClose/dialogClose";
import { useUploadFileMutation } from "../../redux/unity/unity";

export default function Upload({
  raw = false,
  onUpload,
  disabled = false,
  cropDimension = undefined,
  label = "Browse files...",
  children,
}: {
  raw?: boolean;
  onUpload: (e: string) => void;
  disabled?: boolean;
  cropDimension?: { width: number; height: number };
  label?: string;
  children?: ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState();
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [rotation, setRotation] = React.useState(0);
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(null);
  const [croppedImage, setCroppedImage] = React.useState(null);
  const [uploadFile] = useUploadFileMutation();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (cropDimension) {
      const file = acceptedFiles[0];
      const imageDataUrl: any = await readFile(file);
      setValue(imageDataUrl);
      setOpen(true);
    } else {
      upload(acceptedFiles[0]);
    }
  }, []); //eslint-disable-line

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
  });

  const upload = async (file: any) => {
    if (raw) {
      onUpload(file);
    } else {
      await uploadFile(file)
        .unwrap()
        .then((e: any) => {
          const url = `${e}`;
          onUpload(url);
        });
    }
  };

  const onCropComplete = React.useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const showCroppedImage = React.useCallback(async () => {
    try {
      const croppedImage: any = await getCroppedImg(
        value,
        croppedAreaPixels,
        rotation
      );
      const resizedOutput = await ImageResize(croppedImage, {
        format: "png",
        width: cropDimension?.width ?? 0,
        height: cropDimension?.height ?? 0,
        outputType: "blob",
      });

      const resizedBlob = await ensureBlob(resizedOutput);
      upload(resizedBlob);
      setOpen(false);
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, rotation]);

  if (disabled) {
    return <div />;
  }

  return (
    <div style={{ display: "flex" }}>
      <Box
        {...getRootProps()}
        sx={{
          cursor: "pointer",
          "&:hover": { background: "rgba(155,155,155,0.1)" },
        }}
      >
        <input {...getInputProps()} />
        {children ? (
          <Box sx={{ background: isDragActive ? "#2196f355" : undefined }}>
            {children}
          </Box>
        ) : (
          <>
            {isDragActive ? (
              <Chip
                size="sm"
                color="primary"
                sx={{ pt: 0.5, pb: 0.5, background: "white" }}
                startDecorator={<i className="fad fa-chevron-square-down"></i>}
                variant="soft"
              >
                Drop the files here!
              </Chip>
            ) : (
              <Chip
                size="sm"
                color="neutral"
                sx={{ pt: 0.5, pb: 0.5, background: "white" }}
                startDecorator={
                  <i className="fad fa-cloud-upload-alt p-right"></i>
                }
                variant="outlined"
              >
                {label}
              </Chip>
            )}
          </>
        )}
      </Box>
      {cropDimension ? (
        <DialogClose
          onClose={() => setOpen(false)}
          aria-labelledby="simple-dialog-title"
          open={open}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle id="simple-dialog-title">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                flex: 1,
                width: "100%",
              }}
            >
              <Typography variant="h6">Crop the image</Typography>
            </div>
          </DialogTitle>
          <DialogContent>
            <div
              style={{
                width: 600,
                height: 600,
                position: "relative",
                margin: "auto",
              }}
            >
              <Cropper
                image={value}
                crop={crop}
                rotation={rotation}
                zoom={zoom}
                aspect={cropDimension.width / cropDimension.height}
                onCropChange={setCrop}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
              {croppedImage}
            </div>
            <div>
              <Typography variant="overline">Zoom</Typography>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e, zoom) => setZoom(zoom as number)}
              />
            </div>
            <div>
              <Typography variant="overline">Rotation</Typography>
              <Slider
                value={rotation}
                min={0}
                max={360}
                step={1}
                aria-labelledby="Rotation"
                onChange={(e, rotation) => setRotation(rotation as number)}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={showCroppedImage}>Crop</Button>
          </DialogActions>
        </DialogClose>
      ) : (
        []
      )}
    </div>
  );
}

function readFile(file: File) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

async function ensureBlob(
  value: string | Blob | HTMLCanvasElement
): Promise<Blob> {
  if (value instanceof Blob) {
    return value;
  }

  if (typeof value === "string") {
    const response = await fetch(value);
    return response.blob();
  }

  return new Promise((resolve, reject) => {
    value.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Unable to convert canvas to Blob."));
      }
    });
  });
}
